/* eslint-disable no-console */
import { useState, useEffect } from 'react'

/**
 * Hook to detect online/offline status
 * @returns {boolean} - true if online, false if offline
 */
export const useOnlineStatus = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine)

  useEffect(() => {
    const handleOnline = () => {
      console.log('[Network Status] Connection restored')
      setIsOnline(true)
    }

    const handleOffline = () => {
      console.log('[Network Status] Connection lost')
      setIsOnline(false)
    }

    // Add event listeners
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    // Cleanup
    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  return isOnline
}

/**
 * Hook that provides online status with additional network information
 * @returns {object} - { isOnline, connectionType, effectiveType, downlink, rtt, saveData }
 */
export const useNetworkStatus = () => {
  const [networkStatus, setNetworkStatus] = useState(() => {
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection

    return {
      isOnline: navigator.onLine,
      connectionType: connection?.type || 'unknown',
      effectiveType: connection?.effectiveType || 'unknown',
      downlink: connection?.downlink || 0,
      rtt: connection?.rtt || 0,
      saveData: connection?.saveData || false,
    }
  })

  useEffect(() => {
    const updateNetworkStatus = () => {
      const connection =
        navigator.connection || navigator.mozConnection || navigator.webkitConnection

      setNetworkStatus({
        isOnline: navigator.onLine,
        connectionType: connection?.type || 'unknown',
        effectiveType: connection?.effectiveType || 'unknown',
        downlink: connection?.downlink || 0,
        rtt: connection?.rtt || 0,
        saveData: connection?.saveData || false,
      })
    }

    // Update on online/offline events
    window.addEventListener('online', updateNetworkStatus)
    window.addEventListener('offline', updateNetworkStatus)

    // Update on connection change if available
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection
    if (connection) {
      connection.addEventListener('change', updateNetworkStatus)
    }

    // Cleanup
    return () => {
      window.removeEventListener('online', updateNetworkStatus)
      window.removeEventListener('offline', updateNetworkStatus)
      if (connection) {
        connection.removeEventListener('change', updateNetworkStatus)
      }
    }
  }, [])

  return networkStatus
}
